import { FC } from 'react';
import InputControl from '@/components/form/input-control';
import { Box, Grid } from '@mui/material';
import FormSection from '@/components/form/form-section';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { useQuery } from 'react-query';
import useQueryHelper from '@/hooks/use-query-helper';
import { getOrgPolicyV1, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import useToast from '@/hooks/use-toast.hook';
import FormButtons from '@/components/form/form-buttons';
import FormLabel from '@/components/form-label';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { SensitiveDataActionOptions } from '@/lib/models/policy-model-v1/sensitive-data';
import ContentControlsV1 from '@/components/form/policy-v1/content-controls-v1';
import { FieldError } from '@/lib/services';
import MonitorWarningToast from './monitor-warning-toast';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const formModel = new PolicyModelV1();
const fields = ['sensitiveData', 'sensitiveDataSources', 'contentControls'];

const OrgPolicySensitiveDataForm: FC = () => {
  const { toast, errorToast } = useToast();
  const { canChangePolicy } = useFeature();
  const { canViewConfidentialDatasources } = useFeature();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, fieldErrors, error } = await patchPolicyV1(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const { values } = formHandle;

  const query = useQuery([QueryKey.OrgPolicyView], async () => getOrgPolicyV1());
  const { showLoader } = useQueryHelper(query);
  const { data: orgPolicy } = query;

  if (showLoader) {
    return null;
  }

  const entitiesLocked = !values.sensitiveData.detect;

  return (
    <Box>
      <Form formHandle={formHandle} query={query}>
        <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

        <MonitorWarningToast policy={orgPolicy!} />

        <ContentControlsV1
          formHandle={formHandle}
          readonly={!canChangePolicy}
          policy={orgPolicy!}
        />

        <FormSection title="PII Detection Settings" scrollId="detection-settings">
          <FormSectionSubtitle>
            Enable or disable detection of PII entities in requests to public services, and choose
            which action to take when they are detected.
          </FormSectionSubtitle>
          <Grid container alignItems="top">
            <Grid item xs={5}>
              <FormLabel>Enabled</FormLabel>

              <InputControl
                name="sensitiveData.detect"
                type="switch"
                formHandle={formHandle}
                readonly={!canChangePolicy}
              />
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={5}>
              <FormLabel>Action</FormLabel>
              <InputControl
                name="sensitiveData.action"
                type="select"
                options={SensitiveDataActionOptions}
                formHandle={formHandle}
                disabled={!values.sensitiveData.detect}
                readonly={!canChangePolicy}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection
          title="PII Entities"
          locked={entitiesLocked}
          scrollId="pii-entities"
          tooltip="Entities cannot be configured because PII detection is disabled above."
        >
          <FormSectionSubtitle>
            Choose which PII entities to detect, and optionally configure detection mode.
          </FormSectionSubtitle>
          <Grid container alignItems="top">
            <Grid item xs={12} mb={2}>
              <InputControl
                name="sensitiveData.entities"
                type="sd-entity-list-v1"
                formHandle={formHandle}
                disabled={!values.sensitiveData.detect}
                readonly={!canChangePolicy}
                height={400}
                policy={orgPolicy!}
              />
            </Grid>
          </Grid>
        </FormSection>

        {canViewConfidentialDatasources && (
          <FormSection title="Confidential Data Sources">
            <FormSectionSubtitle>
              Choose which data sources contain confidential data. If a request matches content in
              an assigned data source, take the action specified for the confidential data content
              control.
            </FormSectionSubtitle>
            <InputControl
              name="sensitiveDataSources"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="datasources-list-toggle"
            />
          </FormSection>
        )}

        <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
      </Form>
    </Box>
  );
};

export default OrgPolicySensitiveDataForm;
